// API Service for authentication
const API_BASE_URL = '/api';

interface LoginData {
  email: string;
  password: string;
}

/**
 * Données d'inscription — définition unique, partagée avec le contexte
 * d'authentification. Seuls username, email et password sont exigés par le
 * serveur ; le reste est facultatif, et le formulaire d'inscription ne
 * collecte d'ailleurs que le site web en plus des identifiants.
 */
export interface RegisterData {
  username: string;
  email: string;
  password: string;
  activite?: string;
  nom_entreprise?: string;
  ville?: string;
  site_web?: string;
  description_activite?: string;
  /** Réglages initiaux, dont l'activation de la publication automatique SAFTI. */
  preferences?: { auto_publish_safti?: boolean };
}

interface AuthResponse {
  user: any;
  token: string;
  tokenType: string;
}

class AuthService {
  private token: string | null = null;

  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  private async request(endpoint: string, options: RequestInit = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  async login(data: LoginData): Promise<AuthResponse> {
    const result = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    this.token = result.token;
    localStorage.setItem('authToken', result.token);
    localStorage.setItem('user', JSON.stringify(result.user));
    
    return result;
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    const result = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    this.token = result.token;
    localStorage.setItem('authToken', result.token);
    localStorage.setItem('user', JSON.stringify(result.user));
    
    return result;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } finally {
      this.token = null;
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  }

  async getCurrentUser(): Promise<any> {
    if (!this.token) return null;
    
    try {
      const result = await this.request('/auth/me');
      return result.user;
    } catch (error) {
      // Token probably expired
      this.logout();
      return null;
    }
  }

  async forgotPassword(email: string): Promise<void> {
    await this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  getStoredUser(): any {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
}

export default new AuthService();